import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { useBranding } from "@/context/BrandingContext";
import PoweredByFooter from "@/components/PoweredByFooter";

/**
 * Unified Bill Print page.
 * Routes:
 *   /bill/opd/:opdId/print?layout=a4|thermal58|thermal80
 *   /bill/ipd/:ipdId/print?layout=a4|thermal58|thermal80
 */
export default function BillPrint() {
  const { opdId, ipdId } = useParams();
  const [sp] = useSearchParams();
  const layout = sp.get("layout") || "a4";
  const isPreview = sp.get("preview") === "1";
  const [ref, setRef] = useState(null);       // opd or ipd doc
  const [summary, setSummary] = useState(null);
  const [patient, setPatient] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (opdId) {
        const [{ data: opd }, { data: bill }] = await Promise.all([
          api.get(`/opd/${opdId}`),
          api.get(`/opd/${opdId}/billing`),
        ]);
        setRef(opd);
        setSummary(bill);
        if (opd.patient_id) {
          const p = await api.get(`/patients/${opd.patient_id}`);
          setPatient(p.data);
        }
      } else if (ipdId) {
        const { data: ipd } = await api.get(`/ipd/${ipdId}`);
        setRef(ipd);
        // Build synthetic summary from IPD totals
        const items = ipd.charges || [];
        const consultation = items.filter((i) => i.category === "Consultation" || i.category === "Doctor Visit").reduce((s, i) => s + i.amount, 0);
        const procedures = items.filter((i) => i.category === "Procedure" || i.category === "OT" || i.category === "Surgeon").reduce((s, i) => s + i.amount, 0);
        const other = items.filter((i) => !["Consultation", "Doctor Visit", "Procedure", "OT", "Surgeon", "Discount"].includes(i.category)).reduce((s, i) => s + i.amount, 0);
        const discount = -items.filter((i) => i.category === "Discount").reduce((s, i) => s + i.amount, 0);
        const gross = items.filter((i) => i.category !== "Discount").reduce((s, i) => s + i.amount, 0);
        setSummary({
          consultation, procedures, other,
          gross,
          discount_approved: discount,
          net: gross - discount,
          paid: ipd.totals?.paid || 0,
          balance: (gross - discount) - (ipd.totals?.paid || 0),
          items,
          payments: ipd.payments || [],
        });
        if (ipd.patient_id) {
          const p = await api.get(`/patients/${ipd.patient_id}`);
          setPatient(p.data);
        }
      }
    };
    load();
  }, [opdId, ipdId]);

  useEffect(() => {
    if (ref && summary && !isPreview) {
      window.onafterprint = () => window.close();
      setTimeout(() => window.print(), 500);
    }
  }, [ref, summary, isPreview]);

  // Tag <html> with the current print size so the first page uses the
  // correct @page rule (no forced blank page before named-page sheets).
  useEffect(() => {
    const cls = layout === "thermal58" ? "print-mode-t58"
              : layout === "thermal80" ? "print-mode-t80"
              : "print-mode-a4";
    document.documentElement.classList.add(cls);
    return () => document.documentElement.classList.remove(cls);
  }, [layout]);

  // Optional selective print: ?items=id1,id2,... narrows the receipt to those
  // bill_item ids and recomputes gross / net / balance from that subset.
  const displaySummary = useMemo(() => {
    if (!summary) return null;
    const raw = sp.get("items");
    if (!raw) return summary;
    const wanted = new Set(raw.split(",").filter(Boolean));
    const filtered = (summary.items || []).filter((it) => wanted.has(it.id));
    if (filtered.length === 0) return summary;
    const gross = filtered.reduce((s, i) => s + (i.amount || 0), 0);
    // Discount and paid apply to the whole bill; on a partial receipt we drop
    // them so the receipt reflects the items being paid right now.
    return { ...summary, items: filtered, gross, discount_approved: 0, net: gross, balance: gross - 0, paid: 0, partial: true };
  }, [summary, sp]);

  if (!ref || !displaySummary) return <div className="p-8">Loading bill...</div>;

  if (layout === "thermal58" || layout === "thermal80") {
    return <ThermalReceipt width={layout} ref_={ref} summary={displaySummary} patient={patient} isIpd={!!ipdId} />;
  }
  return <A4Bill ref_={ref} summary={displaySummary} patient={patient} isIpd={!!ipdId} />;
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-semibold" : ""}`}>
      <span>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

function A4Bill({ ref_, summary, patient, isIpd }) {
  const brand = useBranding();
  const baseTitle = isIpd ? "IPD FINAL BILL" : "OPD BILL";
  const title = summary.partial ? `${baseTitle} · PARTIAL RECEIPT` : baseTitle;
  const refNo = isIpd ? ref_.ipd_no : ref_.opd_no;
  const phones = (brand.phones || []).filter(Boolean).join(" · ");
  // Extract the padded serial from receipt_no (e.g. RCP00001 → 00001, or the
  // legacy RCP2608000123 → 000123) so we can render a clean "No. 00001" pill.
  const rcp = String(summary.receipt_no || ref_.receipt_no || "").trim();
  const billSerial = rcp.replace(/^RCP/i, "");
  return (
    <div className="min-h-screen bg-slate-100 flex justify-center py-6">
      <div className="print-a4 page-a4 shadow-lg" style={{ fontSize: "12px" }}>
        {/* Header */}
        <div className="flex items-center justify-between border-b-2 border-red-700 pb-3">
          <div className="flex items-center gap-3">
            <img src={brand.logo_url || LOGO_URL} alt={brand.name} className="h-16 object-contain" />
            <div>
              <div className="font-display font-semibold text-lg text-slate-800">{brand.name}</div>
              {brand.tagline && <div className="text-[10px] tracking-widest text-slate-600">{brand.tagline}</div>}
              {brand.address && <div className="text-[10px] text-slate-600">{brand.address}</div>}
              {phones && <div className="text-[10px] text-slate-600">Ph: {phones}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="font-display font-semibold text-lg text-red-700">{title}</div>
            {billSerial && (
              <div className="mt-1 inline-block border border-red-700 rounded-sm px-2 py-0.5 font-mono text-base text-red-700 font-semibold">
                No. {billSerial}
              </div>
            )}
            <div className="text-xs font-mono text-slate-600 mt-1">{refNo}</div>
            <div className="text-[10px] text-slate-500">Printed: {new Date().toLocaleString("en-IN")}</div>
          </div>
        </div>

        {/* Patient Info */}
        <div className="grid grid-cols-3 gap-2 mt-4 text-[11px] border-b pb-3">
          <div><b>Name:</b> {ref_.patient_name}</div>
          <div><b>UHID:</b> <span className="font-mono">{ref_.uhid}</span></div>
          <div><b>Age/Sex:</b> {patient?.age || "-"} / {patient?.gender || "-"}</div>
          <div><b>Mobile:</b> {patient?.mobile || "-"}</div>
          <div><b>Doctor:</b> {ref_.doctor_name}</div>
          <div><b>Department:</b> {ref_.department}</div>
          <div><b>Date:</b> {new Date(ref_.created_at || ref_.admission_date).toLocaleDateString("en-IN")}</div>
          <div><b>PMJAY:</b> {ref_.pmjay ? "YES" : "NO"}</div>
          <div><b>Bill Type:</b> {isIpd ? "IPD" : "OPD"}</div>
        </div>

        {/* Line items */}
        <table className="w-full mt-3 text-[11px]">
          <thead>
            <tr className="bg-slate-100 text-left">
              <th className="p-1 w-8">#</th>
              <th className="p-1">Description</th>
              <th className="p-1 w-20">Category</th>
              <th className="p-1 w-12 text-right">Qty</th>
              <th className="p-1 w-20 text-right">Rate</th>
              <th className="p-1 w-24 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(summary.items || []).filter((i) => i.category !== "Discount").map((it, i) => (
              <tr key={it.id || i} className="border-b border-dashed">
                <td className="p-1">{i + 1}</td>
                <td className="p-1">{it.description}</td>
                <td className="p-1 text-[10px]">{it.category}</td>
                <td className="p-1 text-right font-mono">{it.quantity}</td>
                <td className="p-1 text-right font-mono">{fmtINR(it.rate)}</td>
                <td className="p-1 text-right font-mono">{fmtINR(it.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="mt-4 flex justify-end">
          <div className="w-72 text-[12px] space-y-1 border-t pt-2">
            <Row label="Gross Amount" value={fmtINR(summary.gross)} />
            {summary.discount_approved > 0 && <Row label="Discount (Approved)" value={`- ${fmtINR(summary.discount_approved)}`} />}
            <div className="border-t pt-1">
              <Row label="Net Payable" value={fmtINR(summary.net)} bold />
            </div>
            <Row label="Paid" value={fmtINR(summary.paid)} />
            <div className="border-t pt-1">
              <Row label="Balance Due" value={fmtINR(summary.balance)} bold />
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-6 text-[10px]">
          <div>
            {/* IPD bill never carries the patient/attendant signature (per hospital policy).
               OPD bill honours the per-hospital `show_patient_sig_bill` branding toggle. */}
            {!isIpd && brand.show_patient_sig_bill !== false && (
              <div className="border-t border-slate-400 pt-1 w-40">Patient / Attendant Signature</div>
            )}
          </div>
          <div className="text-right">
            <div className="border-t border-slate-400 pt-1 w-40 ml-auto">For {brand.name}<br/>Authorised Signatory</div>
          </div>
        </div>

        <div className="mt-6 border-t pt-1 text-center text-[9px] text-slate-500">
          This is a computer-generated bill. {brand.name}{brand.address ? ` · ${brand.address}` : ""}{phones ? ` · ${phones}` : ""}
        </div>
        <PoweredByFooter />
      </div>
      <div className="fixed top-2 right-2 no-print flex gap-2">
        <button onClick={() => window.print()} className="bg-blue-700 text-white px-3 py-1 text-sm rounded-sm">Print A4</button>
      </div>
    </div>
  );
}

function ThermalReceipt({ width, ref_, summary, patient, isIpd }) {
  const brand = useBranding();
  const cls = width === "thermal58" ? "print-thermal-58 page-t58" : "print-thermal-80 page-t80";
  const title = summary.partial ? "PART RECEIPT" : (isIpd ? "IPD RECEIPT" : "OPD RECEIPT");
  const refNo = isIpd ? ref_.ipd_no : ref_.opd_no;
  const shortLine = "".padEnd(width === "thermal58" ? 32 : 40, "-");
  const items = (summary.items || []).filter((i) => i.category !== "Discount");
  const phones = (brand.phones || []).filter(Boolean).join(", ");
  const rcp = String(summary.receipt_no || ref_.receipt_no || "").trim();
  const billSerial = rcp.replace(/^RCP/i, "");
  return (
    <div className="min-h-screen bg-slate-100 flex justify-center py-4">
      <div className={`${cls} shadow-lg`}>
        <div className="text-center">
          {brand.logo_url && <img src={brand.logo_url} alt={brand.name} style={{ height: 40, maxWidth: "100%", objectFit: "contain", margin: "0 auto 4px" }} />}
          <div style={{ fontWeight: 700, fontSize: 12 }}>{brand.name}</div>
          {brand.tagline && <div style={{ fontSize: 9 }}>{brand.tagline}</div>}
          {brand.address && <div style={{ fontSize: 8, lineHeight: 1.2 }}>{brand.address}</div>}
          {phones && <div style={{ fontSize: 8 }}>Ph: {phones}</div>}
        </div>
        <div>{shortLine}</div>
        <div className="text-center" style={{ fontWeight: 700 }}>{title}</div>
        {billSerial && <div className="text-center" style={{ fontWeight: 700, fontSize: 14 }}>No. {billSerial}</div>}
        <div>{shortLine}</div>
        <div>OPD : {refNo}</div>
        <div>Date: {new Date().toLocaleString("en-IN")}</div>
        <div>Name: {ref_.patient_name}</div>
        <div>UHID: {ref_.uhid}</div>
        <div>Age/Sex: {patient?.age || "-"} / {patient?.gender || "-"}</div>
        <div>Dr  : {ref_.doctor_name}</div>
        <div>{shortLine}</div>
        {items.map((it, i) => (
          <div key={i} style={{ marginTop: 2 }}>
            <div>{(i + 1)}. {(it.description || "").slice(0, width === "thermal58" ? 26 : 34)}</div>
            <div className="flex justify-between">
              <span>{it.quantity} x {Math.round(it.rate)}</span>
              <span>{Math.round(it.amount)}</span>
            </div>
          </div>
        ))}
        <div>{shortLine}</div>
        <div className="flex justify-between"><span>Gross</span><span>{Math.round(summary.gross)}</span></div>
        {summary.discount_approved > 0 && <div className="flex justify-between"><span>Discount</span><span>- {Math.round(summary.discount_approved)}</span></div>}
        <div className="flex justify-between" style={{ fontWeight: 700 }}><span>Net</span><span>{Math.round(summary.net)}</span></div>
        <div className="flex justify-between"><span>Paid</span><span>{Math.round(summary.paid)}</span></div>
        <div className="flex justify-between" style={{ fontWeight: 700 }}><span>Balance</span><span>{Math.round(summary.balance)}</span></div>
        <div>{shortLine}</div>
        <div className="text-center" style={{ fontSize: 9 }}>Thank you. Get well soon.</div>
        <div className="text-center" style={{ fontSize: 9 }}>* Computer generated *</div>
        <div className="text-center" style={{ fontSize: 7, letterSpacing: "0.1em", color: "#94a3b8", marginTop: 4 }}>
          POWERED BY THE VYOMRA HOS
        </div>
      </div>
      <div className="fixed top-2 right-2 no-print flex gap-2">
        <button onClick={() => window.print()} className="bg-blue-700 text-white px-3 py-1 text-sm rounded-sm">Print {width === "thermal58" ? "58mm" : "80mm"}</button>
      </div>
    </div>
  );
}
